import React, { useEffect, useState } from 'react';
import { vmixInstancesAPI } from '../../lib/api';
import { useConnectionStore } from '../../stores/connectionStore';

// Workspace-level vMix instance registry, managed from Workspace Settings.
// Instances are named host:port targets that any cue action can address; a
// show's own vMix stays the implicit "Primary" when an action names no instance.
export default function VmixInstancesSection() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ label: '', host: '', port: '8088' });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    vmixInstancesAPI.list()
      .then(r => setInstances(r.data))
      .catch(() => setError('Failed to load vMix instances'))
      .finally(() => setLoading(false));
  }, []);

  // Mirror the registry into the shared connection store so the cue-action
  // editor's target dropdown reflects adds/edits/deletes live, without needing
  // the rundown to be reloaded. Skipped while the initial fetch is in flight so
  // an already-populated store isn't briefly cleared.
  useEffect(() => {
    if (loading) return;
    useConnectionStore.getState().setVmixInstances(instances);
  }, [instances, loading]);

  function resetForm() {
    setDraft({ label: '', host: '', port: '8088' });
    setEditingId(null);
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    const label = draft.label.trim();
    const host = draft.host.trim();
    const port = parseInt(draft.port, 10);
    if (!label || !host) { setError('Label and host are required'); return; }
    if (!port || port < 1 || port > 65535) { setError('Port must be 1–65535'); return; }

    setBusy(true);
    setError('');
    try {
      if (editingId) {
        const r = await vmixInstancesAPI.update(editingId, { label, host, port });
        setInstances(prev => prev.map(i => (i.id === editingId ? r.data : i)).sort(byLabel));
      } else {
        const r = await vmixInstancesAPI.create({ label, host, port });
        setInstances(prev => [...prev, r.data].sort(byLabel));
      }
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save instance');
    } finally {
      setBusy(false);
    }
  }

  function startEdit(inst) {
    setEditingId(inst.id);
    setDraft({ label: inst.label, host: inst.host, port: String(inst.port) });
    setError('');
  }

  async function handleDelete(id) {
    if (!confirm('Delete this vMix instance? Actions that target it will fall back to the show’s Primary vMix.')) return;
    try {
      await vmixInstancesAPI.remove(id);
      setInstances(prev => prev.filter(i => i.id !== id));
      if (editingId === id) resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete instance');
    }
  }

  const inputCls = 'bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">vMix Instances</div>
      <p className="text-xs text-gray-600 mb-2 leading-relaxed">
        Named vMix endpoints your cue actions can target. Each show&rsquo;s own vMix is the
        &ldquo;Primary&rdquo; used when an action doesn&rsquo;t pick an instance.
      </p>

      {loading ? (
        <div className="text-gray-500 text-sm py-2">Loading…</div>
      ) : (
        <>
          {instances.length > 0 && (
            <div className="space-y-1 mb-3">
              {instances.map(inst => (
                <div key={inst.id} className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{inst.label}</div>
                    <div className="text-xs text-gray-500 font-mono">{inst.host}:{inst.port}</div>
                  </div>
                  <button onClick={() => startEdit(inst)} className="text-xs text-gray-400 hover:text-blue-400 transition-colors">Edit</button>
                  <button onClick={() => handleDelete(inst.id)} className="text-xs text-gray-400 hover:text-red-400 transition-colors">Delete</button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={draft.label}
                onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
                placeholder="Label (e.g. Stage LR)"
                className={`flex-1 ${inputCls}`}
              />
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={draft.host}
                onChange={e => setDraft(d => ({ ...d, host: e.target.value }))}
                placeholder="Host / IP (e.g. 192.168.1.50)"
                className={`flex-1 font-mono ${inputCls}`}
              />
              <input
                type="number"
                value={draft.port}
                onChange={e => setDraft(d => ({ ...d, port: e.target.value }))}
                placeholder="Port"
                className={`w-20 font-mono ${inputCls}`}
              />
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-xs font-medium rounded transition-colors"
              >
                {busy ? 'Saving…' : editingId ? 'Save changes' : 'Add instance'}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="px-3 py-2 text-xs text-gray-500 hover:text-white transition-colors">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}

function byLabel(a, b) {
  return a.label.localeCompare(b.label);
}
